import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  CreatePolicyRenewal,
  FinishPendingRenewal,
  SelectPolicyRenewalLoaded,
  UpdatePolicyRenewal,
} from 'actions';
import ContactStrip from 'components/contact-strip';
import Title from 'components/title';
import {
  PAGE_VIEW_EVENTS,
  parseNumericalPolicyValues,
  removeEmptyValues,
  trackPolicyRenewalCreated,
  trackPolicyRenewalPendingCompleted,
  trackPolicyRenewalUpdated,
} from 'helpers';
import { IPolicy, IPolicyRenewalState } from 'types';
import PolicyDetailsForm from './components/PolicyDetailsForm';
import styles from './styles/PolicyDetailsContainer.module.scss';
import { Back, Button } from 'components/quashed';
import useTracking from 'hooks/useTracking';

const PolicyRenewalContainer = ({
  hideTitle,
  policy,
  policyProvider,
  selectedRenewal,
}) => {
  useTracking(PAGE_VIEW_EVENTS.RenewalSummaryPage, selectedRenewal);
  const dispatch = useDispatch();
  const { submitting }: IPolicyRenewalState = useSelector(
    R.prop('policyRenewal'),
  );

  const handleRenewalSubmit = (data: Partial<IPolicy>) => {
    const newPolicyData = parseNumericalPolicyValues(removeEmptyValues(data));

    if (selectedRenewal.isUpload) {
      const data = {
        ...selectedRenewal,
        newPolicyData,
        renewalDate: policy.renewalDate,
        policyId: policy.id,
      };
      trackPolicyRenewalPendingCompleted(data);
      return dispatch(FinishPendingRenewal(data));
    }

    if (policy?.policyRenewalAdded) {
      const data = {
        ...selectedRenewal,
        newPolicyData,
        policyId: policy.id,
      };
      trackPolicyRenewalUpdated(data);
      dispatch(UpdatePolicyRenewal(data));
    } else {
      const toUpdate = {
        policyId: policy.id,
        renewalDate: policy.renewalDate,
        premium: policy.premium,
        newPolicyData,
      };

      trackPolicyRenewalCreated(toUpdate);
      dispatch(CreatePolicyRenewal(toUpdate, policy));
    }
  };

  const handleBack = () => {
    dispatch(SelectPolicyRenewalLoaded(null));
  };

  const defaultValues = useMemo(() => {
    const policyToMerge = R.omit(['premium', 'renewalDate'], policy);

    const renewalData = R.omit(
      ['status'],
      selectedRenewal.newPolicyData || selectedRenewal,
    );

    return {
      ...policyToMerge,
      ...renewalData,
      id: policyToMerge.id,
      sk: policyToMerge.sk,
      data: policyToMerge.data,
      processed: true, // at this point processed is always true
    };
  }, [policy, selectedRenewal]);

  return (
    <div className={styles.renewalContainer}>
      {!hideTitle && (
        <div className={styles.header}>
          <Title
            img={policyProvider?.logoUrl}
            subtitle="It’s important to double check your renewal details to make sure they are correct"
            titleClassName={styles.title}
            title={`Renewal - ${policy.policyType}`}
          />
          <ContactStrip className={styles.contact} {...policyProvider} />
        </div>
      )}

      <PolicyDetailsForm
        className={styles.renewalForm}
        errorClassName={styles.error}
        attachments={selectedRenewal.attachments}
        employer={null}
        errorMsg="Some of the information seems to be missing, please review and ensure all your details are entered accurately."
        isOwner={true}
        editing={true}
        mini={false}
        policy={defaultValues}
        showButtons={false}
        title="Renewal details"
        validateOnMount={true}
        onSubmit={handleRenewalSubmit}
      >
        <div className={styles.buttons}>
          <Back
            className={styles.back}
            disabled={submitting}
            onClick={handleBack}
          />
          <Button
            className={styles.submit}
            disabled={submitting}
            type="submit"
            text="Update"
          />
        </div>
      </PolicyDetailsForm>
    </div>
  );
};

export default PolicyRenewalContainer;
